import { useState, useEffect } from 'react'
import { 
  CheckCircle2, AlertTriangle, Upload, Lock, Pencil, Trash2, Plus, 
  BookOpen, FileText, ClipboardList, ExternalLink, Eye, EyeOff, Users,
  X, Check, User, Settings
} from 'lucide-react'
import { useLedger } from '../lib/LedgerContext'
import { categories } from '../data/seed'
import { formatCurrency, formatDate } from '../lib/format'
import LedgerOverlay from '../components/LedgerOverlay'
import { documentsApi, documentPermissionsApi } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../lib/ToastContext'
import { getSignedUrlByPath } from '../lib/storage'
import DocumentUpload from '../components/legal/DocumentUpload'
import DocumentPermissionManager from '../components/DocumentPermissionManager'
import TeamActivityFeed from '../components/TeamActivityFeed'
import { Badge, Button, Card, Spinner } from '../components/ui'
import StaffProfile from '../components/StaffProfile'
import StaffPreferences from '../components/StaffPreferences'

const TIME_WINDOW_MS = 30 * 60 * 1000

export default function StaffWorkspace({ activeTab, currency = 'NGN', dateFormat = 'DD/MM/YYYY', onNavigate }) {
  const { user, company } = useAuth()

  // ✅ If activeTab is 'profile', show StaffProfile
  if (activeTab === 'profile') {
    return (
      <div className="p-4 sm:p-8 lg:p-10 space-y-6 animate-fade-in">
        <h1 className="text-2xl font-bold text-ink">My Profile</h1>
        <StaffProfile />
      </div>
    )
  }

  // ✅ If activeTab is 'preferences', show StaffPreferences
  if (activeTab === 'preferences') {
    return (
      <div className="p-4 sm:p-8 lg:p-10 space-y-6 animate-fade-in">
        <h1 className="text-2xl font-bold text-ink">My Preferences</h1>
        <StaffPreferences 
          prefs={{ country: 'Nigeria', currency: 'NGN', dateFormat: 'DD/MM/YYYY', timezone: 'Africa/Lagos (WAT)' }} 
          onSave={() => {}} 
        />
      </div>
    )
  }

  // ✅ If activeTab is 'activity', show TeamActivityFeed
  if (activeTab === 'activity') {
    return (
      <div className="p-4 sm:p-8 lg:p-10 space-y-6 animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-ink">Team Activity</h1>
          <span className="text-sm text-muted">Recent updates</span>
        </div>
        <TeamActivityFeed companyId={company?.id} />
      </div>
    )
  }

  // ✅ If activeTab is 'docs', show Documents
  if (activeTab === 'docs') {
    return <DocsView companyId={company?.id} userId={user?.id} />
  }

  // ✅ If activeTab is 'tasks', show Tasks (using TasksTab)
  if (activeTab === 'tasks') {
    return <TasksTab isAdmin={false} />
  }

  // ✅ DEFAULT: Show Ledger (activeTab === 'ledger')
  return <LedgerView currency={currency} dateFormat={dateFormat} />
}

// ============================================================
// LEDGER VIEW - Full page
// ============================================================

function LedgerView({ currency = 'NGN', dateFormat = 'DD/MM/YYYY' }) {
  const { transactions, addTransaction, editTransaction, deleteTransaction, lockTransaction } = useLedger()
  const { user } = useAuth()
  const { toast } = useToast()
  const [type, setType] = useState('income')
  const [amount, setAmount] = useState('')
  const [category, setCategory] = useState(categories[0])
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [description, setDescription] = useState('')
  const [reference, setReference] = useState('')
  const [savedFlash, setSavedFlash] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [ledgerOpen, setLedgerOpen] = useState(false)

  const myTransactions = transactions.filter(t => t.created_by === user?.id)

  const reset = () => {
    setType('income'); setAmount(''); setCategory(categories[0])
    setDate(new Date().toISOString().slice(0, 10)); setDescription(''); setReference('')
  }

  const save = (e) => {
    e.preventDefault()
    const amt = Number(amount)
    if (!amount || isNaN(amt) || !description) return
    addTransaction({
      type: type === 'income' ? 'Income' : 'Expense',
      amount: type === 'income' ? Math.abs(amt) : -Math.abs(amt),
      description,
      date,
      category,
      reference,
      created_by: user?.id,
      created_by_role: 'staff',
    })
    reset()
    setSavedFlash(true)
    toast('Transaction added successfully.', 'success')
    setTimeout(() => setSavedFlash(false), 2200)
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-ink">My Ledger</h1>
        <p className="text-sm text-muted mt-1">Track your financial activity</p>
      </div>

      <form onSubmit={save} className="surface p-7">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-base font-semibold text-ink">Add Transaction</h3>
          {savedFlash && <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 animate-fade-in"><CheckCircle2 size={14} /> Transaction saved</span>}
        </div>
        <fieldset className="mb-6">
          <legend className="label-above">Type</legend>
          <div className="flex gap-6">
            {['income', 'expense'].map((t) => (
              <label key={t} className="flex items-center gap-2 text-sm text-ink cursor-pointer capitalize">
                <input type="radio" name="type" value={t} checked={type === t} onChange={() => setType(t)} className="accent-brand" />
                {t === 'income' ? 'Income' : 'Expense'}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div><label className="label-above">Amount (₦)</label><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="input-flat font-mono" placeholder="0" min="0" /></div>
          <div><label className="label-above">Category</label><select value={category} onChange={(e) => setCategory(e.target.value)} className="input-flat">{categories.map((c) => (<option key={c}>{c}</option>))}</select></div>
          <div><label className="label-above">Date</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-flat font-mono" /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-5">
          <div><label className="label-above">Description</label><input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className="input-flat" placeholder="e.g. Software subscription" /></div>
          <div><label className="label-above">Reference number</label><input type="text" value={reference} onChange={(e) => setReference(e.target.value)} className="input-flat font-mono" placeholder="REF-0000" /></div>
        </div>
        <div className="flex gap-3 mt-7">
          <button type="button" onClick={reset} className="btn-secondary">Cancel</button>
          <button type="submit" className="btn-primary">Save Transaction</button>
        </div>
      </form>

      <ImmutableTransactionsTable
        rows={myTransactions}
        onViewAll={() => setLedgerOpen(true)}
        editingId={editingId}
        setEditingId={setEditingId}
        editTransaction={editTransaction}
        deleteTransaction={deleteTransaction}
        lockTransaction={lockTransaction}
        currency={currency}
        dateFormat={dateFormat}
      />

      <LedgerOverlay open={ledgerOpen} onClose={() => setLedgerOpen(false)} role="staff" currency={currency} dateFormat={dateFormat} />
    </div>
  )
}

// ============================================================
// IMMUTABLE TRANSACTIONS TABLE
// ============================================================

function remainingMs(createdAt, locked) {
  if (locked) return 0
  return Math.max(0, TIME_WINDOW_MS - (Date.now() - createdAt))
}

function fmtCountdown(ms) {
  const total = Math.ceil(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function CountdownClock() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}

function ImmutableTransactionsTable({ rows, onViewAll, editingId, setEditingId, editTransaction, deleteTransaction, lockTransaction, currency = 'NGN', dateFormat = 'DD/MM/YYYY' }) {
  const [, force] = useState(0)
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 1000)
    return () => clearInterval(t)
  }, [])

  return (
    <div className="surface">
      <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
        <h3 className="text-sm font-semibold text-ink">My Recent Transactions</h3>
        <button onClick={onViewAll} className="link-secondary">View All</button>
      </div>
      <div className="divide-y divide-neutral-200">
        {rows.map((t) => {
          const rem = remainingMs(t.createdAt, t.locked)
          const editable = rem > 0 && !t.locked
          return (
            <div key={t.id} className="px-6 py-4 grid grid-cols-12 gap-3 items-center text-sm">
              <div className="col-span-3">
                <p className="text-ink">{t.description}</p>
                <p className="font-mono text-[11px] text-neutral-500 mt-0.5">{t.id}</p>
              </div>
              <div className="col-span-2 font-mono text-xs text-neutral-600">{formatDate(t.date, dateFormat)}</div>
              <div className={`col-span-3 font-mono text-xs ${t.amount >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                {formatCurrency(t.amount, currency)}
              </div>
              <div className="col-span-2 flex items-center gap-2">
                {editable ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-amber-300 bg-amber-50 text-amber-700 text-[10px] font-mono">
                    <CountdownClock /> {fmtCountdown(rem)}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-neutral-400 text-[11px]">
                    <Lock size={11} /> Locked
                  </span>
                )}
              </div>
              <div className="col-span-2 flex justify-end gap-2">
                {editable ? (
                  <>
                    <button onClick={() => setEditingId(editingId === t.id ? null : t.id)} className="text-neutral-400 hover:text-brand" aria-label="Edit">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => deleteTransaction(t.id)} className="text-neutral-400 hover:text-rose-600" aria-label="Delete">
                      <Trash2 size={14} />
                    </button>
                    <button onClick={() => lockTransaction(t.id)} className="text-neutral-400 hover:text-ink" aria-label="Lock now">
                      <Lock size={14} />
                    </button>
                  </>
                ) : (
                  <Lock size={14} className="text-neutral-300" />
                )}
              </div>
            </div>
          )
        })}
        {rows.length === 0 && <div className="px-6 py-10 text-center text-sm text-muted">No transactions found.</div>}
      </div>
    </div>
  )
}

// ============================================================
// DOCS VIEW
// ============================================================

function DocsView({ companyId, userId }) {
  const [myDocs, setMyDocs] = useState([])
  const [allDocs, setAllDocs] = useState([])
  const [accessibleDocs, setAccessibleDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [signedUrls, setSignedUrls] = useState({})
  const { user } = useAuth()
  const { toast } = useToast()

  useEffect(() => {
    if (!companyId || !userId) return
    loadDocs()
  }, [companyId, userId])

  const loadDocs = async () => {
    setLoading(true)
    try {
      const { data: myData } = await documentsApi.listForStaff(companyId, userId)
      const { data: allData } = await documentsApi.list(companyId)
      const { data: permsData } = await documentPermissionsApi.getStaffAccessibleDocuments(userId)
      
      setMyDocs(myData || [])
      setAllDocs(allData || [])
      setAccessibleDocs(permsData?.map(p => p.governance_documents) || [])
      
      const urls = {}
      const allDocsToFetch = [...(myData || []), ...(permsData?.map(p => p.governance_documents) || [])]
      for (const doc of allDocsToFetch) {
        if (doc?.file_url) {
          try {
            const url = await getSignedUrlByPath(doc.file_url, 60)
            if (url) urls[doc.id] = url
          } catch {}
        }
      }
      setSignedUrls(urls)
    } catch (err) {
      console.error('Failed to load documents:', err)
    }
    setLoading(false)
  }

  const handleUpload = async (files) => {
    setUploading(true)
    let successCount = 0
    for (const file of files) {
      const result = await documentsApi.upload(file, {
        company_id: companyId,
        label: file.name,
        category: 1,
        uploaded_by: user?.full_name || 'Staff',
      })
      if (!result.error) {
        successCount++
      } else {
        toast(`Failed to upload ${file.name}: ${result.error}`, 'error')
      }
    }
    if (successCount > 0) {
      toast(`Uploaded ${successCount} document${successCount > 1 ? 's' : ''} successfully.`, 'success')
      loadDocs()
    }
    setUploading(false)
  }

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Spinner size="lg" /></div>
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-ink">Documents</h1>
        <p className="text-sm text-muted mt-1">View and manage your documents</p>
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div><h3 className="text-sm font-semibold text-ink">Company Documents</h3><p className="text-xs text-muted mt-0.5">View-only stamps of all documents in the company</p></div>
          <Badge variant="default">{allDocs.length} total</Badge>
        </div>
        <div className="divide-y divide-neutral-200 border border-neutral-200 rounded-lg">
          {allDocs.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-muted">No documents uploaded yet.</div>
          ) : (
            allDocs.map((doc) => {
              const isMine = doc.uploaded_by_id === userId
              const hasAccess = accessibleDocs.some(d => d.id === doc.id)
              const canView = isMine || hasAccess
              return (
                <div key={doc.id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-ink truncate">{doc.label}</p>
                      {isMine && <Badge variant="primary" size="sm">My Upload</Badge>}
                      {hasAccess && !isMine && <Badge variant="success" size="sm"><Eye size={10} className="mr-1" /> Access</Badge>}
                      {!hasAccess && !isMine && <Badge variant="default" size="sm"><EyeOff size={10} className="mr-1" /> Stamp Only</Badge>}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted">
                      <span>Uploaded by: {doc.uploaded_by || 'Unknown'}</span>
                      <span>•</span>
                      <span className="font-mono">{formatDate(doc.created_at)}</span>
                      {doc.status && <><span>•</span><DocStatusBadge status={doc.status} /></>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {canView && signedUrls[doc.id] && <a href={signedUrls[doc.id]} target="_blank" rel="noopener noreferrer" className="text-brand hover:text-brand-dark text-sm"><Eye size={14} /></a>}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div><h3 className="text-sm font-semibold text-ink">My Documents</h3><p className="text-xs text-muted mt-0.5">Documents you've uploaded - full access</p></div>
          <Badge variant="primary">{myDocs.length}</Badge>
        </div>
        <div className="divide-y divide-neutral-200 border border-neutral-200 rounded-lg">
          {myDocs.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-muted">You haven't uploaded any documents yet.</div>
          ) : (
            myDocs.map((doc) => (
              <div key={doc.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{doc.label}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted">
                      <span>Uploaded: {formatDate(doc.created_at)}</span>
                      <span>•</span>
                      <DocStatusBadge status={doc.status} />
                    </div>
                    {signedUrls[doc.id] && <a href={signedUrls[doc.id]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-brand hover:text-brand-dark mt-1"><ExternalLink size={10} /> View file</a>}
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-neutral-100">
                  <DocumentPermissionManager documentId={doc.id} documentLabel={doc.label} />
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-sm font-semibold text-ink mb-4">Upload Document</h3>
        <DocumentUpload onUpload={handleUpload} multiple={false} label="Upload Document" description="Drop your document here or click to browse" />
        {uploading && <div className="mt-3 flex items-center gap-2 text-sm text-muted"><Spinner size="sm" /> Uploading...</div>}
      </Card>
    </div>
  )
}

// ============================================================
// DOC STATUS BADGE
// ============================================================

function DocStatusBadge({ status }) {
  const statusMap = {
    missing: { label: 'Missing', variant: 'default' },
    uploaded: { label: 'Uploaded', variant: 'primary' },
    pending_founder_review: { label: 'Pending Founder Review', variant: 'warning' },
    pending_platform_verification: { label: 'Awaiting Platform Verification', variant: 'warning' },
    verified: { label: 'Verified', variant: 'success' },
    rejected: { label: 'Rejected', variant: 'error' },
    archived: { label: 'Archived', variant: 'default' },
    draft: { label: 'Draft', variant: 'primary' },
    awaiting: { label: 'Awaiting Verification', variant: 'warning' },
  }
  const meta = statusMap[status] || { label: status, variant: 'default' }
  return <Badge variant={meta.variant} size="sm">{meta.label}</Badge>
}

// ============================================================
// TASKS TAB - Exported for AdminDashboard usage
// ============================================================

export function TasksTab({ isAdmin }) {
  const { tasks, addTask, setTaskStatus, deleteTask, staff } = useLedger()
  const { user } = useAuth()
  const { toast } = useToast()
  const [showForm, setShowForm] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [assignee, setAssignee] = useState(staff[0]?.name || '')

  const myTasks = tasks.filter(t => t.assignee === user?.full_name)

  const create = (e) => {
    e.preventDefault()
    if (!title) return
    addTask({ title, description, dueDate, assignee })
    setTitle(''); setDescription(''); setDueDate(''); setShowForm(false)
    toast('Task created successfully.', 'success')
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">My Tasks</h1>
          <p className="text-sm text-muted mt-1">Tasks assigned to you</p>
        </div>
        {isAdmin && <button onClick={() => setShowForm((s) => !s)} className="btn-primary text-xs flex items-center gap-1.5"><Plus size={14} /> New Task</button>}
      </div>

      {isAdmin && showForm && (
        <form onSubmit={create} className="surface p-6 space-y-5 animate-fade-in">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div><label className="label-above">Title</label><input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="input-flat" placeholder="Task title" /></div>
            <div><label className="label-above">Due Date</label><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="input-flat font-mono" /></div>
          </div>
          <div><label className="label-above">Description</label><input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className="input-flat" placeholder="Task description" /></div>
          <div>
            <label className="label-above">Assignee</label>
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="input-flat">
              <option value="">Select staff member…</option>
              {staff.map((s) => (<option key={s.id} value={s.name}>{s.name} — {s.role}</option>))}
            </select>
            {staff.length === 0 && <p className="text-xs text-rose-600 mt-1">No staff members. Add a staff member first.</p>}
          </div>
          <div className="flex gap-3"><button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">Create Task</button></div>
        </form>
      )}

      <div className="surface divide-y divide-neutral-200">
        {myTasks.map((task) => (
          <div key={task.id} className="px-6 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-ink">{task.title}</p>
                <p className="text-xs text-muted mt-1">{task.description}</p>
                <div className="flex items-center gap-4 mt-2 text-[11px] text-neutral-500">
                  <span className="font-mono">{task.id}</span>
                  <span>Assignee: {task.assignee}</span>
                  <span className="font-mono">Due: {task.dueDate ? formatDate(task.dueDate) : '—'}</span>
                </div>
              </div>
              <TaskStatusBadge status={task.status} />
            </div>
            <div className="flex gap-2 mt-3">
              {task.status !== 'Completed' && (
                <button onClick={() => setTaskStatus(task.id, task.status === 'Pending' ? 'In Progress' : 'Completed')} className="btn-ghost text-xs">
                  {task.status === 'Pending' ? 'Mark In Progress' : 'Mark Completed'}
                </button>
              )}
              {isAdmin && (
                <>
                  {task.status === 'Completed' && <button onClick={() => setTaskStatus(task.id, 'Pending')} className="btn-ghost text-xs">Reopen</button>}
                  <button onClick={() => deleteTask(task.id)} className="text-neutral-400 hover:text-rose-600 text-xs px-2">Delete</button>
                </>
              )}
            </div>
          </div>
        ))}
        {myTasks.length === 0 && <div className="px-6 py-10 text-center text-sm text-muted">No tasks assigned to you.</div>}
      </div>
    </div>
  )
}

function TaskStatusBadge({ status }) {
  const map = {
    Pending: 'border-neutral-300 text-neutral-600 bg-neutral-50',
    'In Progress': 'border-amber-300 text-amber-700 bg-amber-50',
    Completed: 'border-emerald-300 text-emerald-700 bg-emerald-50',
  }
  return <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border ${map[status] || ''}`}>{status}</span>
}